import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShellRail, ShellTopBarActions } from '../../shell/AppShell';
import { useCountUp, useReveal } from '../../shell/motion';
import FTLoading from '../../shell/FTLoading';
import { monteCarloAPI } from '../../utils/api';
import { trackCustomEvent } from '../../utils/analytics';
import { runSimulation, applyTradeAdjustments, computeKTCDelta } from '../../utils/monteCarloEngine';
import { defaultLeagueTags } from '../../utils/leagueDynamics';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import TradeSandbox from './TradeSandbox';
import LeagueMoves from './LeagueMoves';
import TeamDetailModal from './TeamDetailModal';
import PlayoffBracket from './PlayoffBracket';
import DraftOrder from './DraftOrder';

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";

// Forecast panel placement: stacked inline on mobile, shell context rail on desktop.
const ForecastSlot = ({ isMobile, children }) => {
  if (isMobile) {
    return <div style={{ background: '#F5F5F7', display: 'flex', flexDirection: 'column' }}>{children}</div>;
  }
  return <ShellRail>{children}</ShellRail>;
};

const NUM_SIMS = 50000; // default — smoother odds than 10k, still hidden under the loader's min-display
const NUM_SIMS_LABEL = '50,000';

// Track viewport width so we can swap to a stacked layout on phones.
function useIsMobile(breakpoint = 760) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// ── scatter geometry ──────────────────────────────────────────────────────────
const SW = 640, SH = 360;
const SP = { l: 48, r: 60, t: 26, b: 40 };   // extra top room for bubbles near 100%, right room for labels
const spW = SW - SP.l - SP.r;
const spH = SH - SP.t - SP.b;

// ── fan geometry ──────────────────────────────────────────────────────────────
const FW = 320, FH = 148;
const FP = { l: 28, r: 14, t: 8, b: 26 };

function pctFmt(p) { return `${Math.round((p || 0) * 100)}`; }

// Plain-language read of a trade's playoff-odds delta — a simple threshold
// call on a number that already exists, not a new stat.
function tradeVerdict(dPl) {
  if (dPl == null || Number.isNaN(dPl)) return null;
  if (dPl > 2) return { text: 'Worth it.', tone: 'up' };
  if (dPl < -2) return { text: 'A step back.', tone: 'down' };
  return { text: 'Marginal.', tone: 'flat' };
}

// ── Hero stat value — counts from the previous value to the new one on
// change (instead of snapping) and briefly flashes color. Direction is
// computed by the caller (from its own before/after values) rather than the
// hook's internal `direction`, which reflects the *target* value once the
// count is underway, not the change's sign.
function HeroStatValue({ value, decimals, tickDirection }) {
  const { display, flashing } = useCountUp(value, { duration: 700, decimals, enabled: value != null });
  return <span className={flashing ? `ft-tick ${tickDirection}` : 'ft-tick'}>{value != null ? display : '—'}</span>;
}

// ── Fun loading state — football driving down the field ────────────────────────
const SIM_MESSAGES = [
  'Kicking off the season…',
  'Playing out every matchup…',
  'Week 7 upsets brewing…',
  'Tracking the playoff race…',
  'Sorting the standings…',
  'Simulating the bracket…',
  'Crowning champions…',
  'Counting the outcomes…',
];

function SimLoading({ numSims, teams = [] }) {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMsgIdx(i => (i + 1) % SIM_MESSAGES.length), 1100);
    return () => clearInterval(t);
  }, []);

  // The league's own teams churn up and down a standings ladder — a literal
  // picture of "we're replaying your season thousands of times." Every moving
  // part is a transform/opacity CSS animation, so it runs on the compositor and
  // keeps going smoothly even while the sim blocks the main thread (all browsers
  // + mobile, no worker, never frozen).
  const ROW_H = 42, GAP = 7, PITCH = ROW_H + GAP;
  const rows = (teams || []).slice(0, 6);
  const containerH = rows.length ? rows.length * PITCH - GAP : 0;
  // Top rows drift downward, bottom rows drift upward, so the churn stays in frame.
  const SHUF = ['shfA', 'shfB', 'shfC', 'shfD', 'shfE', 'shfF'];

  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: 380, fontFamily: ff }}>
      <style>{`
        @keyframes shfA { 0%,100%{transform:translateY(0)} 24%{transform:translateY(${PITCH}px)} 56%{transform:translateY(${2 * PITCH}px)} 82%{transform:translateY(${PITCH}px)} }
        @keyframes shfB { 0%,100%{transform:translateY(0)} 26%{transform:translateY(${PITCH}px)} 60%{transform:translateY(0)} 84%{transform:translateY(-${PITCH}px)} }
        @keyframes shfC { 0%,100%{transform:translateY(0)} 28%{transform:translateY(-${PITCH}px)} 54%{transform:translateY(${PITCH}px)} 80%{transform:translateY(0)} }
        @keyframes shfD { 0%,100%{transform:translateY(0)} 22%{transform:translateY(${PITCH}px)} 58%{transform:translateY(-${PITCH}px)} 84%{transform:translateY(0)} }
        @keyframes shfE { 0%,100%{transform:translateY(0)} 30%{transform:translateY(-${PITCH}px)} 62%{transform:translateY(0)} 86%{transform:translateY(${PITCH}px)} }
        @keyframes shfF { 0%,100%{transform:translateY(0)} 26%{transform:translateY(-${PITCH}px)} 58%{transform:translateY(-${2 * PITCH}px)} 84%{transform:translateY(-${PITCH}px)} }
        @keyframes ftDot { 0%,72%,100%{opacity:.25;transform:translateY(0)} 36%{opacity:1;transform:translateY(-3px)} }
        @keyframes barP { 0%,100%{transform:scaleX(0.4)} 50%{transform:scaleX(1)} }
      `}</style>

      {rows.length > 0 ? (
        <div style={{ position: 'relative', height: containerH, marginBottom: 22 }}>
          {rows.map((t, i) => {
            const initial = (t.displayName || '?').trim().charAt(0).toUpperCase();
            return (
              <div key={t.rosterId} style={{
                position: 'absolute', top: i * PITCH, left: 0, right: 0, height: ROW_H,
                animation: `${SHUF[i % 6]} ${2.6 + (i % 3) * 0.4}s cubic-bezier(0.65,0,0.35,1) ${i * 0.12}s infinite`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: '100%', background: '#fff', border: '1px solid #ECECEE', borderRadius: 10, padding: '0 12px', boxShadow: '0 3px 12px rgba(0,0,0,0.06)' }}>
                  {t.avatarUrl
                    ? <img src={t.avatarUrl} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#E5E5EA' }} onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
                    : <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: '#E5E5EA', color: '#6E6E73', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{initial}</div>}
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1D1D1F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.displayName}</span>
                  <div style={{ flex: 1 }} />
                  <div style={{ width: 44, height: 4, background: '#EEE', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                    <div style={{ height: '100%', width: '100%', background: '#1D1D1F', borderRadius: 2, transformOrigin: 'left', animation: `barP ${1.1 + i * 0.12}s ease-in-out infinite` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 40, marginBottom: 16 }}>🏈</div>
      )}

      <div style={{ fontSize: 19, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.01em' }}>
        Simulating {numSims.toLocaleString()} seasons
        <span style={{ display: 'inline-block', marginLeft: 2 }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{ display: 'inline-block', animation: `ftDot 1.4s ease-in-out ${i * 0.18}s infinite` }}>.</span>
          ))}
        </span>
      </div>
      <div style={{ fontSize: 14, color: '#86868B', marginTop: 8, minHeight: 18 }}>
        {SIM_MESSAGES[msgIdx]}
      </div>
    </div>
  );
}

// "Justin Jefferson" → "Jefferson"; pick labels like "2027 1st" pass through.
function shortName(name) {
  if (!name) return '';
  if (/^\d{4}\s/.test(name)) return name; // pick label
  const parts = name.trim().split(' ');
  return parts.length > 1 ? parts[parts.length - 1] : name;
}

// ── Scatter chart ─────────────────────────────────────────────────────────────
function ScatterChart({ bootstrap, results, afterResults, view, selectedTeamId, onSelectTeam, myRosterId }) {
  const [hover, setHover] = useState(null); // {x, y, name} — bubble hover tooltip
  if (!bootstrap || !results) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C7C7CC', fontSize: 13, fontFamily: ff }}>
      Run simulation to see results
    </div>
  );

  const allMaxPF = bootstrap.teams.map(t => results.teams[t.rosterId]?.maxPointsFor?.p50 || 0);
  const minMP = Math.max(0, Math.min(...allMaxPF) - 50);
  const maxMP = Math.max(...allMaxPF) + 50;

  const mpToX = mp => SP.l + ((mp - minMP) / (maxMP - minMP)) * spW;
  const plToY = pl => SP.t + spH - (pl / 100) * spH;
  // Smaller, capped bubbles reduce overlap. Floor so 0% title is still visible.
  const chToR = ch => Math.max(6, Math.min(17, 6 + ch * 0.42));

  const data = view === 'after' && afterResults ? afterResults : results;
  const baseData = results;

  // Build paths for your team's before→after arrow
  const myBase = baseData.teams[myRosterId];
  const myAfter = afterResults?.teams[myRosterId];
  const myBasePL = myBase ? myBase.playoffProbability * 100 : 0;
  const myAfterPL = myAfter ? myAfter.playoffProbability * 100 : 0;
  const myBaseMP = myBase?.maxPointsFor?.p50 || 0;
  const myAfterMP = myAfter?.maxPointsFor?.p50 || myBaseMP;

  let svg = `<defs><clipPath id="scc"><rect x="${SP.l}" y="${SP.t}" width="${spW}" height="${spH}"/></clipPath></defs>`;

  // Grid lines
  [0, 25, 50, 75, 100].forEach(pl => {
    const y = plToY(pl);
    svg += `<line x1="${SP.l}" y1="${y}" x2="${SP.l + spW}" y2="${y}" stroke="rgba(0,0,0,0.05)" stroke-width="1"/>`;
    svg += `<text x="${SP.l - 6}" y="${y + 4}" text-anchor="end" font-size="10" fill="#C7C7CC" font-family="${ff}">${pl}%</text>`;
  });

  // X axis ticks
  const mpRange = maxMP - minMP;
  const mpStep = mpRange > 800 ? 250 : 200;
  for (let mp = Math.ceil(minMP / mpStep) * mpStep; mp <= maxMP; mp += mpStep) {
    const x = mpToX(mp);
    svg += `<line x1="${x}" y1="${SP.t}" x2="${x}" y2="${SP.t + spH}" stroke="rgba(0,0,0,0.05)" stroke-width="1"/>`;
    svg += `<text x="${x}" y="${SP.t + spH + 14}" text-anchor="middle" font-size="10" fill="#C7C7CC" font-family="${ff}">${mp >= 1000 ? (mp/1000).toFixed(1)+'k' : mp}</text>`;
  }

  // Axis labels
  svg += `<text x="${SP.l + spW / 2}" y="${SH - 2}" text-anchor="middle" font-size="9" fill="#D2D2D7" letter-spacing="1.5" font-family="${ff}" font-weight="700">MAX POINTS FOR</text>`;
  svg += `<text x="10" y="${SP.t + spH / 2}" text-anchor="middle" font-size="9" fill="#D2D2D7" letter-spacing="1.5" font-family="${ff}" font-weight="700" transform="rotate(-90,10,${SP.t + spH / 2})">PLAYOFF %</text>`;

  // Coin-flip reference: above this line a team is more likely than not to make
  // it. It's a heuristic, not the literal cutline (which depends on standings).
  const cutY = plToY(50);
  svg += `<line x1="${SP.l}" y1="${cutY}" x2="${SP.l + spW}" y2="${cutY}" stroke="rgba(0,0,0,0.12)" stroke-width="1" stroke-dasharray="4,4"/>`;
  svg += `<text x="${SP.l + spW - 4}" y="${cutY - 5}" text-anchor="end" font-size="9" fill="rgba(0,0,0,0.22)" font-family="${ff}" font-weight="600">50% — coin flip to make it</text>`;

  // Quadrant labels
  svg += `<text x="${SP.l + spW * 0.72}" y="${SP.t + 18}" font-size="10" fill="rgba(0,0,0,0.08)" font-weight="700" font-family="${ff}">Contenders</text>`;
  svg += `<text x="${SP.l + 8}" y="${SP.t + spH - 8}" font-size="10" fill="rgba(0,0,0,0.08)" font-weight="700" font-family="${ff}">Rebuilding</text>`;

  // ── Compute clamped bubble positions for every team ──────────────────────
  const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const clampCy = (rawCy, r) => Math.max(SP.t + r + 2, Math.min(SP.t + spH - r - 2, rawCy));
  const clampCx = (rawCx, r) => Math.max(SP.l + r + 2, Math.min(SP.l + spW - r - 2, rawCx));

  // ── My team's marker position (before + after) — rendered outside the raw
  // SVG string as real JSX below so a CSS transition can slide it, rather
  // than snapping when the trade toggle or active scenario changes.
  const myTeamObj = bootstrap.teams.find(t => t.rosterId === myRosterId);
  const meBaseR = chToR((myBase?.championshipProbability || 0) * 100);
  const meAfterR = myAfter ? chToR(myAfter.championshipProbability * 100) : meBaseR;
  const meBaseCx = clampCx(mpToX(myBaseMP), meBaseR + 12);
  const meBaseCy = clampCy(plToY(myBasePL), meBaseR + 12);
  const meAfterCx = clampCx(mpToX(myAfterMP), meAfterR + 12);
  const meAfterCy = clampCy(plToY(myAfterPL), meAfterR + 12);
  const meShowAfter = view === 'after' && afterResults && myAfter;
  const meCx = meShowAfter ? meAfterCx : meBaseCx;
  const meCy = meShowAfter ? meAfterCy : meBaseCy;
  const meR = meShowAfter ? meAfterR : meBaseR;
  const meFull = myTeamObj?.displayName || (myRosterId != null ? `Team ${myRosterId}` : '');
  const meLabelText = meFull.length > 16 ? meFull.slice(0, 15) + '…' : meFull;
  const meFs = 11;
  let meLabelY = -(meR + 6);
  if (meCy + meLabelY < SP.t + 10) meLabelY = meR + meFs + 4;

  const nodes = bootstrap.teams.map(team => {
    const tr = data.teams[team.rosterId];
    if (!tr) return null;
    const pl = tr.playoffProbability * 100;
    const ch = tr.championshipProbability * 100;
    const mp = tr.maxPointsFor?.p50 || 0;
    const r = chToR(ch);
    const cx = clampCx(mpToX(mp), r + (team.rosterId === myRosterId ? 12 : 0));
    const cy = clampCy(plToY(pl), r + (team.rosterId === myRosterId ? 12 : 0));
    return { team, tr, pl, ch, mp, r, cx, cy, isMe: team.rosterId === myRosterId, isSel: selectedTeamId === team.rosterId };
  }).filter(Boolean);

  // ── Bubbles (clipped to plot) ────────────────────────────────────────────
  svg += `<g clip-path="url(#scc)">`;

  // Arrow: before→after for my team
  if (view === 'after' && afterResults && myBase && myAfter) {
    const x1 = mpToX(myBaseMP), y1 = clampCy(plToY(myBasePL), chToR(myBase.championshipProbability * 100));
    const x2 = mpToX(myAfterMP), y2 = clampCy(plToY(myAfterPL), chToR(myAfter.championshipProbability * 100));
    svg += `<circle cx="${x1}" cy="${y1}" r="${chToR(myBase.championshipProbability * 100)}" fill="none" stroke="#AEAEB2" stroke-width="1.5" stroke-dasharray="3,3" opacity="0.5"/>`;
    svg += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#C7C7CC" stroke-width="1" stroke-dasharray="2,3" opacity="0.5"/>`;
  }

  // Draw every team except me here — my team's bubble is rendered as real JSX
  // below (not baked into this string) so its position can animate.
  const drawOrder = nodes.filter(n => !n.isMe);
  drawOrder.forEach(n => {
    const { cx, cy, r, isSel, team } = n;
    const ringColor = isSel ? '#1D1D1F' : 'rgba(0,0,0,0.18)';
    const ringW = isSel ? 2.5 : 1.2;
    const cid = `av${team.rosterId}`;
    if (team.avatarUrl) {
      svg += `<clipPath id="${cid}"><circle cx="${cx}" cy="${cy}" r="${r}"/></clipPath>`;
      svg += `<image href="${esc(team.avatarUrl)}" x="${cx - r}" y="${cy - r}" width="${2 * r}" height="${2 * r}" clip-path="url(#${cid})" preserveAspectRatio="xMidYMid slice" style="cursor:pointer" data-id="${team.rosterId}"/>`;
      svg += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${ringColor}" stroke-width="${ringW}" style="cursor:pointer" data-id="${team.rosterId}"/>`;
    } else {
      const initial = esc((team.displayName || '?').trim().charAt(0).toUpperCase());
      svg += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#E5E5EA" stroke="${ringColor}" stroke-width="${ringW}" style="cursor:pointer" data-id="${team.rosterId}"/>`;
      svg += `<text x="${cx}" y="${cy + r * 0.35}" text-anchor="middle" font-size="${Math.round(r)}" font-weight="700" fill="#6E6E73" style="pointer-events:none" font-family="${ff}">${initial}</text>`;
    }
  });

  svg += `</g>`;

  // ── Labels (outside clip) ────────────────────────────────────────────────
  // Labeling every bubble gets chaotic when teams cluster. Only label YOUR team
  // (always — drawn as JSX below) and any selected team — full names live in
  // the team list, and clicking a bubble selects it to reveal its name here.
  nodes.filter(n => !n.isMe && n.isSel).forEach(n => {
    const full = n.team.displayName || `Team ${n.team.rosterId}`;
    const text = full.length > 16 ? full.slice(0, 15) + '…' : full;
    const fs = 10;
    let ly = n.cy - n.r - 6;
    if (ly < SP.t + 10) ly = n.cy + n.r + fs + 4; // flip below if near top
    svg += `<text x="${n.cx}" y="${ly}" text-anchor="middle" font-size="${fs}" font-weight="700" fill="#1D1D1F" style="pointer-events:none" font-family="${ff}">${esc(text)}</text>`;
  });

  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseMove={e => {
        const el = e.target.closest && e.target.closest('[data-id]');
        if (el) {
          const id = parseInt(el.getAttribute('data-id'));
          const t = bootstrap.teams.find(x => x.rosterId === id);
          setHover({ x: e.clientX, y: e.clientY, name: t?.displayName || 'Team' });
        } else if (hover) setHover(null);
      }}
      onMouseLeave={() => setHover(null)}
      onClick={e => {
        const el = e.target.closest && e.target.closest('[data-id]');
        if (el) onSelectTeam(parseInt(el.getAttribute('data-id')));
      }}
    >
      <svg
        viewBox={`0 0 ${SW} ${SH}`}
        style={{ width: '100%', height: '100%', display: 'block' }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {/* My team's marker — real JSX (not baked into the string above) so its
          cx/cy/r can transition smoothly when the before/after view or the
          active scenario changes, instead of snapping to the new spot. */}
      {myBase && myTeamObj && (
        <svg
          viewBox={`0 0 ${SW} ${SH}`}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          aria-hidden="true"
        >
          <g style={{ transform: `translate(${meCx}px, ${meCy}px)`, transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <circle r={meR + 11} fill="rgba(29,29,31,0.05)" style={{ transition: 'r 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }} />
            {myTeamObj.avatarUrl ? (
              <>
                <clipPath id={`ovme-clip-${myRosterId}`}><circle r={meR} style={{ transition: 'r 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }} /></clipPath>
                <image
                  href={myTeamObj.avatarUrl}
                  x={-meR} y={-meR} width={meR * 2} height={meR * 2}
                  clipPath={`url(#ovme-clip-${myRosterId})`}
                  preserveAspectRatio="xMidYMid slice"
                  data-id={myRosterId}
                  style={{
                    cursor: 'pointer', pointerEvents: 'auto',
                    transition: 'x 0.6s cubic-bezier(0.16, 1, 0.3, 1), y 0.6s cubic-bezier(0.16, 1, 0.3, 1), width 0.6s cubic-bezier(0.16, 1, 0.3, 1), height 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                />
                <circle r={meR} fill="none" stroke="#1D1D1F" strokeWidth={2.5} data-id={myRosterId}
                  style={{ cursor: 'pointer', pointerEvents: 'auto', transition: 'r 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }} />
              </>
            ) : (
              <>
                <circle r={meR} fill="#E5E5EA" stroke="#1D1D1F" strokeWidth={2.5} data-id={myRosterId}
                  style={{ cursor: 'pointer', pointerEvents: 'auto', transition: 'r 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                <text y={meR * 0.35} textAnchor="middle" fontSize={Math.round(meR)} fontWeight="700" fill="#6E6E73" fontFamily={ff} style={{ pointerEvents: 'none' }}>
                  {(myTeamObj.displayName || '?').trim().charAt(0).toUpperCase()}
                </text>
              </>
            )}
            <circle r={meR + 3.5} fill="none" stroke="#1D1D1F" strokeWidth={1.5} opacity={0.25} style={{ pointerEvents: 'none', transition: 'r 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }} />
            <text y={meLabelY} textAnchor="middle" fontSize={11} fontWeight="700" fill="#1D1D1F" fontFamily={ff} style={{ pointerEvents: 'none' }}>
              {meLabelText}
            </text>
          </g>
        </svg>
      )}
      {hover && (
        <div style={{ position: 'fixed', left: hover.x, top: hover.y - 46, transform: 'translateX(-50%)', background: '#1D1D1F', color: '#fff', borderRadius: 9, padding: '7px 11px', textAlign: 'center', pointerEvents: 'none', zIndex: 60, whiteSpace: 'nowrap', fontFamily: ff, boxShadow: '0 8px 22px rgba(0,0,0,0.22)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{hover.name}</div>
          <div style={{ fontSize: 9.5, fontWeight: 500, color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>click for more →</div>
        </div>
      )}
    </div>
  );
}

// ── Fan chart ─────────────────────────────────────────────────────────────────
function FanChart({ results, afterResults, view, myRosterId, regularSeasonWeeks, currentWeek }) {
  const fxOf = w => FP.l + (w / regularSeasonWeeks) * (FW - FP.l - FP.r);
  const fyOf = pl => FP.t + (FH - FP.t - FP.b) - (Math.max(0, Math.min(100, pl)) / 100) * (FH - FP.t - FP.b);

  if (!results) {
    return (
      <svg width="100%" height={FH} viewBox={`0 0 ${FW} ${FH}`} style={{ display: 'block' }}>
        <text x={FW / 2} y={FH / 2 + 5} textAnchor="middle" fontSize="11" fill="#D2D2D7" fontFamily={ff} fontWeight="500">Run simulation to see projection</text>
      </svg>
    );
  }

  const myBase = results.teams[myRosterId];
  const myAfter = afterResults?.teams[myRosterId];
  if (!myBase) return null;

  // Build week sequence: week 0 = "now" (aggregate odds), weeks 1..N = per-week projections
  const baseOdds = myBase.playoffProbability * 100;
  const afterOdds = myAfter ? myAfter.playoffProbability * 100 : null;

  const bSeq = [
    { p10: baseOdds, p25: baseOdds, p50: baseOdds, p75: baseOdds, p90: baseOdds },
    ...(myBase.weeklyPlayoff || []),
  ].map(p => ({ p10: p.p10, p25: p.p25, p50: p.p50, p75: p.p75, p90: p.p90 }));

  const aSeq = myAfter ? [
    { p10: afterOdds, p25: afterOdds, p50: afterOdds, p75: afterOdds, p90: afterOdds },
    ...(myAfter.weeklyPlayoff || []),
  ].map(p => ({ p10: p.p10, p25: p.p25, p50: p.p50, p75: p.p75, p90: p.p90 })) : null;

  function band(seq, tk, bk) {
    if (seq.length < 2) return '';
    const top = seq.map((p, w) => `${fxOf(w).toFixed(1)},${fyOf(p[tk] || p.p50).toFixed(1)}`);
    const bot = [...seq].reverse().map((p, ri, a) => `${fxOf(a.length - 1 - ri).toFixed(1)},${fyOf(p[bk] || p.p50).toFixed(1)}`);
    return [...top, ...bot].join(' ');
  }

  function line(seq, key) {
    return seq.map((p, w) => `${fxOf(w).toFixed(1)},${fyOf(p[key] || p.p50).toFixed(1)}`).join(' ');
  }

  let svg = `<defs><clipPath id="fc2"><rect x="${FP.l}" y="0" width="${FW - FP.l - FP.r + 20}" height="${FH}"/></clipPath></defs>`;

  // Grid
  [0, 50, 100].forEach(pl => {
    const y = fyOf(pl);
    svg += `<line x1="${FP.l}" y1="${y}" x2="${FW - FP.r}" y2="${y}" stroke="rgba(0,0,0,0.05)" stroke-width="1"/>`;
    svg += `<text x="${FP.l - 4}" y="${y + 3.5}" text-anchor="end" font-size="8.5" fill="#D2D2D7" font-family="${ff}">${pl}%</text>`;
  });

  // Cutline
  const cutY = fyOf(50);
  svg += `<line x1="${FP.l}" y1="${cutY}" x2="${FW - FP.r}" y2="${cutY}" stroke="rgba(0,0,0,0.08)" stroke-width="1" stroke-dasharray="3,3"/>`;

  // Week labels
  for (let w = 0; w <= regularSeasonWeeks; w += 2) {
    svg += `<text x="${fxOf(w).toFixed(1)}" y="${FH - 4}" text-anchor="middle" font-size="8" fill="#C7C7CC" font-family="${ff}">${w === 0 ? 'Now' : `W${(currentWeek || 0) + w}`}</text>`;
  }

  // Now line
  svg += `<line x1="${fxOf(0)}" y1="${FP.t}" x2="${fxOf(0)}" y2="${FP.t + FH - FP.t - FP.b}" stroke="rgba(0,0,0,0.1)" stroke-width="1" stroke-dasharray="2,3"/>`;

  svg += `<g clip-path="url(#fc2)">`;

  // Base bands
  const bp90 = band(bSeq, 'p90', 'p10');
  const bp75 = band(bSeq, 'p75', 'p25');
  if (bp90) svg += `<polygon points="${bp90}" fill="rgba(174,174,178,0.15)"/>`;
  if (bp75) svg += `<polygon points="${bp75}" fill="rgba(174,174,178,0.32)"/>`;
  svg += `<polyline points="${line(bSeq, 'p50')}" fill="none" stroke="#AEAEB2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;

  // After bands (darker)
  if (aSeq && (view === 'after')) {
    const ap90 = band(aSeq, 'p90', 'p10');
    const ap75 = band(aSeq, 'p75', 'p25');
    if (ap90) svg += `<polygon points="${ap90}" fill="rgba(29,29,31,0.06)"/>`;
    if (ap75) svg += `<polygon points="${ap75}" fill="rgba(29,29,31,0.14)"/>`;
    svg += `<polyline points="${line(aSeq, 'p50')}" fill="none" stroke="#1D1D1F" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>`;
  }

  // End dots
  svg += `<circle cx="${fxOf(0)}" cy="${fyOf(baseOdds)}" r="3.5" fill="#AEAEB2"/>`;
  if (aSeq && view === 'after') svg += `<circle cx="${fxOf(0)}" cy="${fyOf(afterOdds || baseOdds)}" r="3.5" fill="#1D1D1F"/>`;

  // End labels
  const bEnd = bSeq[bSeq.length - 1]?.p50;
  const aEnd = aSeq?.[aSeq.length - 1]?.p50;
  if (bEnd != null) svg += `<text x="${fxOf(regularSeasonWeeks) + 4}" y="${fyOf(bEnd) + 3.5}" font-size="10" font-weight="700" fill="#AEAEB2" font-family="${ff}">${Math.round(bEnd)}%</text>`;
  if (aEnd != null && view === 'after') svg += `<text x="${fxOf(regularSeasonWeeks) + 4}" y="${fyOf(aEnd) + 3.5}" font-size="10" font-weight="700" fill="#1D1D1F" font-family="${ff}">${Math.round(aEnd)}%</text>`;

  svg += `</g>`;

  return (
    <svg width="100%" height={FH} viewBox={`0 0 ${FW} ${FH}`} style={{ display: 'block' }}
      dangerouslySetInnerHTML={{ __html: svg }} />
  );
}

// ── Team list row ─────────────────────────────────────────────────────────────
function TeamList({ bootstrap, results, afterResults, view, myRosterId, selectedTeamId, onSelectTeam }) {
  const [hoverHdr, setHoverHdr] = useState(null); // {x, y, text} — column header explainer
  if (!bootstrap || !results) return null;
  const data = view === 'after' && afterResults ? afterResults : results;

  const showHdr = (e, text) => { const r = e.currentTarget.getBoundingClientRect(); setHoverHdr({ x: r.left + r.width / 2, y: r.bottom + 6, text }); };
  const hdrCell = { textAlign: 'right', cursor: 'default' };

  const weeks = bootstrap.regularSeasonWeeks || 14;
  const rows = bootstrap.teams.map(team => {
    const tr = data.teams[team.rosterId];
    const basetr = results.teams[team.rosterId];
    const pl = tr?.playoffProbability * 100 || 0;
    const ch = tr?.championshipProbability * 100 || 0;
    const mp = tr?.maxPointsFor?.p50 || 0;
    const basePl = basetr?.playoffProbability * 100 || 0;
    const dPl = afterResults ? (pl - basePl) : 0;
    // Projected median end-of-season record from the sim.
    const projWins = Math.round(tr?.wins?.p50 ?? 0);
    const projRecord = `${projWins}-${weeks - projWins}`;
    return { team, pl, ch, mp, dPl, projRecord };
  }).sort((a, b) => b.pl - a.pl);

  const allMp = rows.map(r => r.mp);
  const minMp = Math.min(...allMp), maxMp = Math.max(...allMp);

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '22px 1fr 42px 42px 52px', gap: 4, padding: '7px 16px 4px', fontSize: 8.5, letterSpacing: '0.3px', textTransform: 'uppercase', color: '#86868B', fontWeight: 700, fontFamily: ff }}>
        <div /><div>Team</div>
        <div style={hdrCell} onMouseEnter={e => showHdr(e, 'Chance to make the playoffs')} onMouseLeave={() => setHoverHdr(null)}>Playoff</div>
        <div style={hdrCell} onMouseEnter={e => showHdr(e, 'Chance to win the championship')} onMouseLeave={() => setHoverHdr(null)}>Champ</div>
        <div style={hdrCell} onMouseEnter={e => showHdr(e, 'Max points for — total season scoring with optimal lineups')} onMouseLeave={() => setHoverHdr(null)}>MPF</div>
      </div>
      <div style={{ padding: '0 16px 6px', fontSize: 9.5, color: '#C7C7CC', fontFamily: ff }}>Tap a team for its full forecast →</div>

      {rows.map(({ team, pl, ch, mp, dPl, projRecord }, rank) => {
        const isMe = team.rosterId === myRosterId;
        const isSel = selectedTeamId === team.rosterId;
        const mpPct = maxMp > minMp ? ((mp - minMp) / (maxMp - minMp)) * 100 : 50;
        const dcls = dPl > 0.5 ? '#1a9640' : dPl < -0.5 ? '#c0392b' : '#C7C7CC';
        return (
          <div
            key={team.rosterId}
            onClick={() => onSelectTeam(team.rosterId)}
            onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#F5F5F7'; }}
            onMouseLeave={e => { e.currentTarget.style.background = isSel ? '#F5F5F7' : isMe ? 'rgba(29,29,31,0.02)' : 'transparent'; }}
            style={{
              display: 'grid', gridTemplateColumns: '22px 1fr 42px 42px 52px', gap: 4,
              alignItems: 'center', padding: '7px 16px',
              cursor: 'pointer', transition: 'background 0.12s',
              borderLeft: `2.5px solid ${isMe ? 'rgba(29,29,31,0.3)' : isSel ? '#1D1D1F' : 'transparent'}`,
              background: isSel ? '#F5F5F7' : isMe ? 'rgba(29,29,31,0.02)' : 'transparent',
              borderTop: rank > 0 ? '1px solid rgba(0,0,0,0.04)' : 'none',
              fontFamily: ff,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: rank < 3 ? '#C88B00' : '#D2D2D7', textAlign: 'center' }}>
              {rank + 1}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#1D1D1F' }}>
                {team.displayName}
              </div>
              <div style={{ fontSize: 9, color: '#86868B', display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
                <span title="Projected median record">~{projRecord}</span>
                {isMe && <span style={{ fontSize: 7, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', background: 'rgba(0,0,0,0.08)', color: '#6E6E73', padding: '1px 4px', borderRadius: 3 }}>You</span>}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.01em' }}>{Math.round(pl)}%</span>
              {afterResults && Math.abs(dPl) > 0.3 && (
                <span style={{ fontSize: 9, fontWeight: 700, color: dcls }}>
                  {dPl > 0 ? '+' : ''}{dPl.toFixed(0)}%
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <span style={{ fontSize: 9, color: '#86868B' }}>{ch.toFixed(1)}%</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <span style={{ fontSize: 9, fontWeight: 600, color: '#86868B' }}>{mp >= 1000 ? (mp / 1000).toFixed(1) + 'k' : Math.round(mp)}</span>
              <div style={{ width: 36, height: 3, background: '#F5F5F7', borderRadius: 2, position: 'relative' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${mpPct}%`, background: isMe ? '#1D1D1F' : '#D2D2D7', borderRadius: 2 }} />
              </div>
            </div>
          </div>
        );
      })}

      {/* Column-header explainer */}
      {hoverHdr && (
        <div style={{ position: 'fixed', top: hoverHdr.y, left: hoverHdr.x, transform: 'translateX(-50%)', zIndex: 200, background: '#1D1D1F', color: '#fff', borderRadius: 8, padding: '7px 11px', fontSize: 12, fontWeight: 500, fontFamily: ff, boxShadow: '0 8px 24px rgba(0,0,0,0.22)', maxWidth: 220, textAlign: 'center', pointerEvents: 'none', lineHeight: 1.35 }}>
          {hoverHdr.text}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MonteCarloSimulator() {
  const { leagueId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, requestMagicLink } = useAuth();
  const { setContextLeague, setLeagueSwitchHandler } = useShell();
  const isMobile = useIsMobile();
  const username = searchParams.get('username');
  const sleeperUserId = searchParams.get('userId');
  // Back should return to the user's league list, not the empty start screen.
  const backTo = username ? `/simulate?username=${encodeURIComponent(username)}` : '/simulate';

  const [bootstrap, setBootstrap] = useState(null);
  const [baseResults, setBaseResults] = useState(null);

  // Push the simulated league into the shell so the topbar chip matches what's
  // on screen. (There is no persisted global league anymore.)
  useEffect(() => {
    if (!bootstrap) return undefined;
    setContextLeague({
      leagueId,
      name: bootstrap.leagueName,
      numTeams: bootstrap.teams?.length || 0,
      isSuperFlex: !!bootstrap.isSuperFlex,
      ppr: bootstrap.scoringSettings?.rec || 0,
      tePremium: bootstrap.scoringSettings?.bonus_rec_te || 0,
    });
    return () => setContextLeague(null);
  }, [bootstrap, leagueId, setContextLeague]);

  // Topbar league dropdown navigates straight to that league's simulator.
  useEffect(() => {
    setLeagueSwitchHandler((league) => {
      const params = new URLSearchParams();
      if (league.name) params.set('name', league.name);
      if (sleeperUserId) params.set('userId', sleeperUserId);
      if (username) params.set('username', username);
      navigate(`/simulate/${league.leagueId}?${params.toString()}`);
    });
    return () => setLeagueSwitchHandler(null);
  }, [setLeagueSwitchHandler, sleeperUserId, username, navigate]);
  // Each scenario: { id, label, adjustments, summary, results }
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState('baseline');
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [simDone, setSimDone] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('before'); // 'before' | 'after'
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [detailTeamId, setDetailTeamId] = useState(null); // opens the team-detail modal
  const [sandboxOpen, setSandboxOpen] = useState(false);
  const [savedSims, setSavedSims] = useState([]);
  const [numSims, setNumSims] = useState(NUM_SIMS); // 10k default, up to 100k
  const [leftView, setLeftView] = useState('scatter'); // 'scatter' | 'bracket'
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [authModal, setAuthModal] = useState(null); // null | 'email' | 'sent' — quick login to save
  const [authEmail, setAuthEmail] = useState('');
  const [autoSaveAfterRun, setAutoSaveAfterRun] = useState(false); // save once the restored scenarios finish running
  const [pendingConfig, setPendingConfig] = useState(null); // saved trade_config awaiting hydration
  const [pendingLeague, setPendingLeague] = useState(null); // saved league_moves awaiting hydration
  const [pinnedRosterId, setPinnedRosterId] = useState(null); // "you" restored from a saved sim (URL has no userId/username)
  const [hydrated, setHydrated] = useState(false);
  const [hoverChip, setHoverChip] = useState(null); // {id, give, get, x, y} — full-trade tooltip, fixed-positioned to escape the scroll container

  // mouseleave can never fire if the chip re-renders away or an overlay opens
  // mid-hover — so while the tooltip is up, any pointer movement outside a
  // scenario chip (or any click/scroll) dismisses it.
  useEffect(() => {
    if (!hoverChip) return undefined;
    const clear = () => setHoverChip(null);
    const onMove = (e) => {
      if (!(e.target instanceof Element) || !e.target.closest('[data-scenario-chip]')) clear();
    };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerdown', clear, true);
    window.addEventListener('scroll', clear, true);
    return () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerdown', clear, true);
      window.removeEventListener('scroll', clear, true);
    };
  }, [hoverChip]);
  // ── League moves: model the rest of the league trading ──
  const [leagueOpen, setLeagueOpen] = useState(false); // the League moves panel
  const [leagueTags, setLeagueTags] = useState(null); // { rosterId: 'buyer'|'seller'|'pat' }
  const [pureBaseResults, setPureBaseResults] = useState(null); // chaos-off baseline (tag defaults + reference)
  // What's actually injected into every sim: { moves, trades, summaries }
  const [appliedLeague, setAppliedLeague] = useState({ moves: 0, trades: [], summaries: [] });
  // One-time nudge after the first run: re-runs come from the top-right button.
  const [runHint, setRunHint] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false); // "how this works" explainer

  const activeScenario = scenarios.find(s => s.id === activeScenarioId) || null;
  const tradeResults = activeScenario?.results || null;
  const tradeAdjustments = activeScenario?.adjustments || [];

  const myTeam = useMemo(() => {
    if (!bootstrap) return null;
    // 0) A saved sim pins your team by roster id (its URL carries no userId/username)
    if (pinnedRosterId != null) {
      const pinned = bootstrap.teams.find(t => t.rosterId === pinnedRosterId);
      if (pinned) return pinned;
    }
    // 1) Match by immutable Sleeper userId (most reliable)
    if (sleeperUserId) {
      const byId = bootstrap.teams.find(t => t.sleeperUserId === sleeperUserId);
      if (byId) return byId;
    }
    // 2) Match by Sleeper account name (ownerName) or team name (displayName)
    if (username) {
      const u = username.toLowerCase();
      const byName = bootstrap.teams.find(t =>
        t.ownerName?.toLowerCase() === u || t.displayName?.toLowerCase() === u
      );
      if (byName) return byName;
    }
    // 3) Fallback — first team (shouldn't normally happen)
    return bootstrap.teams[0];
  }, [bootstrap, sleeperUserId, username, pinnedRosterId]);
  const myRosterId = myTeam?.rosterId;

  // My team's before/after — computed here (rather than down by the JSX)
  // so the reveal hooks below, which depend on it, can be declared ahead of
  // the loading/error early-returns.
  const myBase = baseResults?.teams[myRosterId];
  const myAfter = tradeResults?.teams[myRosterId];
  const hasTradeData = !!(tradeResults && myAfter);
  const dPl = hasTradeData ? Math.round((myAfter.playoffProbability - myBase.playoffProbability) * 100) : null;
  const dCh = hasTradeData ? ((myAfter.championshipProbability - myBase.championshipProbability) * 100).toFixed(1) : null;
  const verdict = hasTradeData ? tradeVerdict(dPl) : null;

  // Reveal timing for the delight layer: the count-up starts immediately,
  // the delta pill catches up ~350ms later, the verdict settles in after
  // that, and the promoted trade CTA fades in once a baseline exists.
  const deltaReveal = useReveal(hasTradeData, 350);
  const verdictReveal = useReveal(!!verdict, 700);
  const tradeCtaReveal = useReveal(simDone && scenarios.length === 0, 60);

  // Load bootstrap
  useEffect(() => {
    monteCarloAPI.getBootstrap(leagueId)
      .then(res => { setBootstrap(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || 'Failed to load league data'); setLoading(false); });
  }, [leagueId]);

  // Load saved sims
  useEffect(() => {
    if (user) monteCarloAPI.listSimulations().then(r => setSavedSims(r.data.simulations || [])).catch(() => {});
  }, [user]);

  // Build a human-readable summary of a trade ("London → Jefferson") from adjustments.
  const summarize = useCallback((adjustments) => {
    const dists = bootstrap?.playerDistributions || {};
    const a = adjustments[0] || {};
    const give = [
      ...(a.giveAway || []).map(i => dists[i.playerId]?.name || 'Player'),
      ...(a.picksGiven || []).map(p => `${p.season} ${p.round === 1 ? '1st' : p.round === 2 ? '2nd' : p.round === 3 ? '3rd' : p.round + 'th'}`),
    ];
    const get = [
      ...(a.receive || []).map(i => dists[i.playerId]?.name || 'Player'),
      ...(a.picksReceived || []).map(p => `${p.season} ${p.round === 1 ? '1st' : p.round === 2 ? '2nd' : p.round === 3 ? '3rd' : p.round + 'th'}`),
    ];
    return { give, get };
  }, [bootstrap]);

  // Run baseline + every scenario at the current sim count.
  // The 420ms pre-delay lets the football-field animation play before the
  // (blocking) compute; a min-display guard keeps fast runs from flickering.
  // Re-run the baseline + every scenario, injecting the league's own trades as a
  // shared prefix so "before" and "after" stay apples-to-apples. When no league
  // trades are active, the baseline doubles as the pure reference for tag defaults.
  const runAllWith = useCallback((leagueTrades) => {
    if (!bootstrap) return;
    setSimulating(true);
    const start = (typeof performance !== 'undefined' ? performance.now() : 0);
    setTimeout(() => {
      try {
        const base = runSimulation(bootstrap, { numSimulations: numSims, tradeAdjustments: leagueTrades });
        setBaseResults(base);
        if (!leagueTrades || leagueTrades.length === 0) setPureBaseResults(base);
        setScenarios(prev => prev.map(s => ({
          ...s,
          results: runSimulation(bootstrap, { numSimulations: numSims, tradeAdjustments: [...leagueTrades, ...s.adjustments] }),
        })));
        setSimDone(true);
      } catch (e) { console.error(e); }
      finally {
        const elapsed = (typeof performance !== 'undefined' ? performance.now() : 0) - start;
        setTimeout(() => setSimulating(false), Math.max(0, 900 - elapsed));
      }
    }, 420);
  }, [bootstrap, numSims]);

  const handleRunSim = useCallback(() => {
    // Record the run for admin engagement analytics. visitor/user identity is
    // attached server-side; fire-and-forget so it never blocks the sim.
    trackCustomEvent('simulation_run', {
      leagueId,
      leagueName: bootstrap?.leagueName,
      season: bootstrap?.season,
      numSims,
      leagueMoves: appliedLeague?.moves || 0,
      scenarios: scenarios.length,
      // who ran it (when known — set by the landing flow / saved-sim restore)
      sleeperUsername: username || undefined,
      sleeperUserId: sleeperUserId || undefined,
      teamOwner: myTeam?.ownerName || undefined,
      teamName: myTeam?.displayName || undefined,
    });
    runAllWith(appliedLeague.trades);
  }, [runAllWith, appliedLeague, leagueId, bootstrap, numSims, scenarios, username, sleeperUserId, myTeam]);

  // Default each opponent's Buyer/Seller/Pat from the pure baseline once it exists.
  useEffect(() => {
    if (pureBaseResults && bootstrap && myRosterId != null && !leagueTags) {
      setLeagueTags(defaultLeagueTags(bootstrap.teams, pureBaseResults, myRosterId));
    }
  }, [pureBaseResults, bootstrap, myRosterId, leagueTags]);

  // Commit the league panel's slider + tags: generate the trades, drop any the
  // user X'd out, and re-run with the rest.
  // The panel sends the already-curated set (locked + unlocked, minus X'd-out),
  // so we just freeze it and run — no regeneration here keeps it exactly in sync.
  const applyLeague = useCallback((p) => {
    if (!bootstrap || !p) return;
    setLeagueTags(p.tags);
    setAppliedLeague({
      moves: p.moves,
      trades: p.trades || [],
      summaries: p.summaries || [],
      excluded: p.excluded || [],
      locked: p.locked || [],
      seed: p.seed,
    });
    setLeagueOpen(false);
    runAllWith(p.trades || []);
  }, [bootstrap, runAllWith]);

  const clearLeague = useCallback(() => {
    setAppliedLeague({ moves: 0, trades: [], summaries: [], excluded: [], locked: [], seed: undefined });
    runAllWith([]);
  }, [runAllWith]);

  // After the very first completed run, point people at the top-right Run button
  // (where every re-run kicks off). Shows once, then never again. Ref-guarded so
  // StrictMode's double-invoke doesn't immediately tear it down.
  const runHintFired = useRef(false);
  useEffect(() => {
    if (!simDone || runHintFired.current) return;
    let seen = false;
    try { seen = localStorage.getItem('mcRunHintSeen') === '1'; } catch { /* ignore */ }
    if (seen) return;
    runHintFired.current = true;
    try { localStorage.setItem('mcRunHintSeen', '1'); } catch { /* ignore */ }
    setRunHint(true);
  }, [simDone]);
  // Auto-dismiss the nudge on its own timer (separate effect = StrictMode-safe).
  useEffect(() => {
    if (!runHint) return;
    const t = setTimeout(() => setRunHint(false), 8000);
    return () => clearTimeout(t);
  }, [runHint]);

  // Apply a freshly-built trade as a NEW scenario, run it, and switch to it.
  const handleApplyTrade = useCallback((adjustments) => {
    if (!bootstrap || adjustments.length === 0) return;
    setSimulating(true);
    setSandboxOpen(false);
    // Pre-delay lets the loader paint before the (blocking) compute; min-display
    // keeps the animation on screen long enough to read even on fast leagues.
    const start = (typeof performance !== 'undefined' ? performance.now() : 0);
    setTimeout(() => {
      try {
        const base = baseResults || runSimulation(bootstrap, { numSimulations: numSims, tradeAdjustments: appliedLeague.trades });
        if (!baseResults) setBaseResults(base);
        // The user's trade stacks on top of whatever the league is doing.
        const results = runSimulation(bootstrap, { numSimulations: numSims, tradeAdjustments: [...appliedLeague.trades, ...adjustments] });
        const id = `trade-${Date.now()}`;
        setScenarios(prev => {
          const label = `Trade ${String.fromCharCode(65 + prev.length)}`; // A, B, C…
          return [...prev, { id, label, adjustments, summary: summarize(adjustments), results }];
        });
        setActiveScenarioId(id);
        setSimDone(true);
        setView('after');
        // Distinct from `simulation_run` — this is the trade-sandbox flow
        // specifically completing, so usage of the (previously invisible)
        // what-if tool is actually measurable.
        const myBaseR = base.teams?.[myRosterId];
        const myAfterR = results.teams?.[myRosterId];
        trackCustomEvent('trade_sandbox_simulated', {
          leagueId,
          leagueName: bootstrap?.leagueName,
          season: bootstrap?.season,
          numSims,
          scenarioCount: scenarios.length + 1,
          playoffOddsDelta: (myBaseR && myAfterR)
            ? Math.round((myAfterR.playoffProbability - myBaseR.playoffProbability) * 100)
            : undefined,
          sleeperUsername: username || undefined,
          sleeperUserId: sleeperUserId || undefined,
        });
      } catch (e) { console.error(e); }
      finally {
        const elapsed = (typeof performance !== 'undefined' ? performance.now() : 0) - start;
        setTimeout(() => setSimulating(false), Math.max(0, 900 - elapsed));
      }
    }, 360);
  }, [bootstrap, numSims, baseResults, summarize, appliedLeague, leagueId, myRosterId, scenarios.length, username, sleeperUserId]);

  // Opening the trade sandbox is the moment we most want to measure — this is
  // the "invisible" flow the promoted CTA + the scenario-row pill both open.
  const handleOpenSandbox = useCallback(() => {
    trackCustomEvent('trade_sandbox_opened', {
      leagueId,
      leagueName: bootstrap?.leagueName,
      season: bootstrap?.season,
      hasBaseline: simDone,
      existingScenarios: scenarios.length,
      sleeperUsername: username || undefined,
      sleeperUserId: sleeperUserId || undefined,
    });
    setSandboxOpen(true);
  }, [leagueId, bootstrap, simDone, scenarios.length, username, sleeperUserId]);

  const selectBaseline = useCallback(() => { setActiveScenarioId('baseline'); setView('before'); }, []);
  const selectScenario = useCallback((id) => { setActiveScenarioId(id); setView('after'); }, []);
  const removeScenario = useCallback((id) => {
    setScenarios(prev => prev.filter(s => s.id !== id));
    setActiveScenarioId(cur => cur === id ? 'baseline' : cur);
    setView('before');
  }, []);

  // Clicking a team (scatter or list) highlights it AND opens its detail modal.
  const handleSelectTeam = useCallback((id) => {
    setSelectedTeamId(id);
    setDetailTeamId(id);
  }, []);

  // The League-moves market the user simulated against, captured so a saved sim
  // reproduces the exact league reshaping (not just the user's own trades).
  const leagueMovesPayload = useCallback(() => (
    appliedLeague.trades && appliedLeague.trades.length
      ? {
        moves: appliedLeague.moves,
        seed: appliedLeague.seed,
        excluded: appliedLeague.excluded || [],
        locked: appliedLeague.locked || [],
        trades: appliedLeague.trades,
        summaries: appliedLeague.summaries || [],
        tags: leagueTags || null,
      }
      : null
  ), [appliedLeague, leagueTags]);

  // Persist the full set of trade scenarios (their trades, not the computed
  // results — those recompute on open against the latest rosters/projections).
  const handleSave = async () => {
    if (!user || !baseResults || !myRosterId || !scenarios.length) return;
    setSaveState('saving');
    const myBase = baseResults.teams[myRosterId];
    const myAfter = tradeResults?.teams[myRosterId];
    try {
      const sim = await monteCarloAPI.saveSimulation({
        sleeperLeagueId: leagueId,
        season: bootstrap.season,
        leagueName: bootstrap.leagueName,
        name: scenarios.map(s => (s.summary?.get || []).map(shortName).join('/')).filter(Boolean).join(' · ') || null,
        tradeConfig: scenarios.map(s => ({ label: s.label, adjustments: s.adjustments, summary: s.summary })),
        leagueMoves: leagueMovesPayload(),
        resultsSnapshot: {
          before: { playoffProbability: myBase.playoffProbability, championshipProbability: myBase.championshipProbability },
          after: myAfter ? { playoffProbability: myAfter.playoffProbability, championshipProbability: myAfter.championshipProbability } : null,
          ktcDelta: null,
          // Pin "you" so reopening the saved sim (its URL has no userId/username)
          // doesn't fall back to the first team in the league.
          myRosterId,
          sleeperUserId: sleeperUserId || myTeam?.sleeperUserId || null,
        },
      });
      setSavedSims(prev => [sim.data.simulation, ...prev]);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2200);
    } catch (e) { console.error(e); setSaveState('idle'); }
  };

  // Stash the current scenarios so they can be saved after the magic-link round-trip.
  const stashPendingScenarios = useCallback(() => {
    try {
      localStorage.setItem('pendingMonteCarlo', JSON.stringify({
        sleeperLeagueId: leagueId,
        username: username || '',
        myRosterId,
        tradeConfig: scenarios.map(s => ({ label: s.label, adjustments: s.adjustments, summary: s.summary })),
        leagueMoves: leagueMovesPayload(),
      }));
    } catch { /* ignore */ }
  }, [leagueId, username, scenarios, leagueMovesPayload]);

  // Save click: logged in → save now; logged out → open the quick email modal.
  const handleSaveClick = () => {
    if (user) { handleSave(); return; }
    stashPendingScenarios();
    setAuthModal('email');
  };

  const handleEmailSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!authEmail.trim()) return;
    stashPendingScenarios();
    try {
      await requestMagicLink(authEmail.trim(), { monteCarloAction: 'save' });
      setAuthModal('sent');
    } catch (err) { console.error(err); }
  };

  // Returning from the magic link (?restorePending=1) — recompute the stashed
  // scenarios and auto-save them now that the user is authenticated.
  useEffect(() => {
    if (!searchParams.get('restorePending') || !user) return;
    try {
      const raw = localStorage.getItem('pendingMonteCarlo');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.tradeConfig?.length) { setPendingConfig(parsed.tradeConfig); setAutoSaveAfterRun(true); }
        if (parsed.leagueMoves) setPendingLeague(parsed.leagueMoves);
        if (parsed.myRosterId != null) setPinnedRosterId(parsed.myRosterId);
      }
    } catch { /* ignore */ }
    localStorage.removeItem('pendingMonteCarlo');
  }, [searchParams, user]);

  // Once the restored scenarios have run, save them automatically (one shot).
  useEffect(() => {
    if (autoSaveAfterRun && user && simDone && scenarios.length > 0 && scenarios[0].results && saveState === 'idle') {
      setAutoSaveAfterRun(false);
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveAfterRun, user, simDone, scenarios]);

  // ── Restore a saved set of scenarios (?sim=<id>) and recompute them ──
  useEffect(() => {
    const simId = searchParams.get('sim');
    if (!simId) return;
    monteCarloAPI.getSimulation(simId)
      .then(r => {
        const sim = r.data.simulation;
        setPendingConfig(sim?.trade_config || []);
        if (sim?.league_moves) setPendingLeague(sim.league_moves);
        // Restore which team is "you" (the saved-sim URL carries no identity).
        if (sim?.results_snapshot?.myRosterId != null) setPinnedRosterId(sim.results_snapshot.myRosterId);
      })
      .catch(() => {});
  }, [searchParams]);

  const scenariosFromConfig = useCallback((config) => {
    if (!Array.isArray(config) || !config.length) return [];
    // New format: array of { label, adjustments, summary }
    if (config[0] && config[0].adjustments) {
      return config.map((c, i) => ({
        id: `saved-${i}-${Date.now()}`,
        label: c.label || `Trade ${String.fromCharCode(65 + i)}`,
        adjustments: c.adjustments,
        summary: c.summary || summarize(c.adjustments),
        results: null,
      }));
    }
    // Legacy format: the whole array is one scenario's adjustments
    return [{ id: `saved-0-${Date.now()}`, label: 'Trade A', adjustments: config, summary: summarize(config), results: null }];
  }, [summarize]);

  // Once the league is loaded and we have a saved config, hydrate + run it —
  // injecting any saved League-moves market so before/after match what was saved.
  useEffect(() => {
    if (!bootstrap || !pendingConfig || hydrated) return;
    setHydrated(true);
    const hydratedScenarios = scenariosFromConfig(pendingConfig);
    const leagueTrades = (pendingLeague?.trades) || [];
    setSimulating(true);
    setTimeout(() => {
      try {
        const base = runSimulation(bootstrap, { numSimulations: numSims, tradeAdjustments: leagueTrades });
        setBaseResults(base);
        if (!leagueTrades.length) setPureBaseResults(base);
        const withResults = hydratedScenarios.map(s => ({
          ...s,
          results: runSimulation(bootstrap, { numSimulations: numSims, tradeAdjustments: [...leagueTrades, ...s.adjustments] }),
        }));
        setScenarios(withResults);
        setActiveScenarioId(withResults[0]?.id || 'baseline');
        // Restore the League-moves chip + panel state from the saved market.
        if (pendingLeague) {
          setAppliedLeague({
            moves: pendingLeague.moves || 0,
            trades: leagueTrades,
            summaries: pendingLeague.summaries || [],
            excluded: pendingLeague.excluded || [],
            locked: pendingLeague.locked || [],
            seed: pendingLeague.seed,
          });
          if (pendingLeague.tags) setLeagueTags(pendingLeague.tags);
        }
        setSimDone(true);
        setView(withResults.length ? 'after' : 'before');
      } catch (e) { console.error(e); }
      finally { setSimulating(false); }
    }, 420);
  }, [bootstrap, pendingConfig, pendingLeague, hydrated, scenariosFromConfig, numSims]);

  // ── Loading states ────────────────────────────────────────────────────────
  // Light nav matching the simulator's own header — keeps loading/error from
  // flashing the dark app header before the white sim view appears.
  const lightNav = (
    <div style={{ height: 50, display: 'flex', alignItems: 'center', gap: 12, padding: '0 24px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.07)', fontFamily: ff }}>
      <button onClick={() => navigate(backTo)} style={{ background: 'none', border: 'none', color: '#86868B', fontSize: 13, cursor: 'pointer', fontFamily: ff }}>←</button>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.01em' }}>{searchParams.get('name') || 'League'}</span>
    </div>
  );
  if (loading) {
    return (
      <div style={{ minHeight: 'calc(100vh - 60px)', background: '#F5F5F7', fontFamily: ff }}>
        {lightNav}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 190px)' }}>
          <FTLoading label={`Loading ${searchParams.get('name') || 'league'}…`} style={{ padding: 0 }} />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ minHeight: 'calc(100vh - 60px)', background: '#F5F5F7', fontFamily: ff }}>
        {lightNav}
        <div style={{ padding: '80px 20px', textAlign: 'center' }}>
          <div style={{ color: '#FF3B30', fontSize: 17 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>{bootstrap.leagueName} — Simulate — Fantasy Taco</title></Helmet>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 0; }
      `}</style>

      {/* ── Controls live in the shell topbar — no second bar ── */}
      <ShellTopBarActions>
          <button
            onClick={() => setInfoOpen(true)}
            aria-label="How this works"
            title="How this works"
            style={{ flexShrink: 0, width: 19, height: 19, borderRadius: '50%', border: '1px solid #D2D2D7', background: '#fff', color: '#86868B', fontSize: 12, fontStyle: 'italic', fontWeight: 700, cursor: 'pointer', fontFamily: 'Georgia, serif', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
          >i</button>
          {/* Before/After toggle — only show when trade is simulated */}
          {hasTradeData && (
            <div style={{ display: 'flex', background: '#F5F5F7', borderRadius: 980, padding: 3, gap: 2 }}>
              {['before', 'after'].map(v => (
                <button key={v} onClick={() => setView(v)} style={{
                  border: 'none', background: view === v ? '#000' : 'transparent',
                  color: view === v ? '#fff' : '#86868B',
                  fontSize: 12, fontWeight: 600, padding: isMobile ? '5px 10px' : '5px 16px', borderRadius: 980,
                  cursor: 'pointer', transition: 'all 0.18s', fontFamily: ff, whiteSpace: 'nowrap',
                }}>
                  {v === 'before' ? 'Before' : isMobile ? 'After' : 'After trade'}
                </button>
              ))}
            </div>
          )}

          {/* Sim-count selector */}
          {!isMobile && (
            <div style={{ display: 'flex', background: '#F5F5F7', borderRadius: 980, padding: 3, gap: 2 }}>
              {[[50000, '50K'], [100000, '100K']].map(([n, label]) => (
                <button key={n} onClick={() => { setNumSims(n); setSimDone(false); }} title={`${n.toLocaleString()} simulations`} style={{
                  border: 'none', background: numSims === n ? '#000' : 'transparent',
                  color: numSims === n ? '#fff' : '#86868B',
                  fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 980,
                  cursor: 'pointer', transition: 'all 0.18s', fontFamily: ff,
                }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* Save scenarios — logged-out users get a quick email modal first */}
          {simDone && scenarios.length > 0 && (
            <button
              onClick={handleSaveClick}
              disabled={saveState !== 'idle'}
              title="Save these trade scenarios to your account"
              style={{
                background: saveState === 'saved' ? 'rgba(52,199,89,0.12)' : '#F5F5F7',
                color: saveState === 'saved' ? '#1a9640' : '#1D1D1F',
                border: 'none', borderRadius: 980, padding: '8px 14px',
                fontSize: 12, fontWeight: 600, cursor: saveState === 'idle' ? 'pointer' : 'default',
                fontFamily: ff, transition: 'all 0.18s', whiteSpace: 'nowrap',
              }}
            >
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Saved' : isMobile ? 'Save' : 'Save scenarios'}
            </button>
          )}
          <button
            onClick={handleRunSim}
            disabled={simulating}
            style={{
              background: simDone ? '#34C759' : '#000',
              color: '#fff', border: 'none', borderRadius: 980, padding: '8px 18px',
              fontSize: 12, fontWeight: 600, cursor: simulating ? 'default' : 'pointer',
              opacity: simulating ? 0.5 : 1, fontFamily: ff, transition: 'all 0.18s',
              display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
            }}
          >
            {simulating ? (
              <><div style={{ width: 12, height: 12, border: '1.5px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.65s linear infinite' }} /> {isMobile ? '' : 'Simulating…'}</>
            ) : simDone ? '✓ Done' : isMobile ? 'Run' : `Run ${(numSims/1000)}K sims`}
          </button>
      </ShellTopBarActions>

      {/* ── Two-panel layout (desktop) / stacked (mobile) ──
          Heights account for the shell topbar (60px) above this view. */}
      <div style={isMobile
        ? { display: 'flex', flexDirection: 'column', background: '#F5F5F7', minHeight: 'calc(100vh - 60px)', position: 'relative' }
        : { display: 'grid', gridTemplateColumns: '1fr', height: 'calc(100vh - 60px)', background: '#F5F5F7', position: 'relative' }
      }>

        {/* One-time nudge: re-runs come from the Run button up here */}
        {runHint && (
          <div
            onClick={() => setRunHint(false)}
            style={{ position: 'absolute', top: 6, right: 14, zIndex: 250, background: '#1D1D1F', color: '#fff', borderRadius: 12, padding: '11px 15px', fontSize: 13, fontWeight: 500, fontFamily: ff, maxWidth: 230, lineHeight: 1.4, cursor: 'pointer', boxShadow: '0 14px 36px rgba(0,0,0,0.3)' }}
          >
            <div style={{ position: 'absolute', top: -5, right: 30, width: 11, height: 11, background: '#1D1D1F', transform: 'rotate(45deg)', borderRadius: 2 }} />
            Tweak a trade, then re-run from <strong style={{ color: '#fff' }}>Run ↑</strong>
          </div>
        )}

        {/* ── Left: Trade builder + Scatter ── */}
        <div style={isMobile
          ? { display: 'flex', flexDirection: 'column', background: '#fff', position: 'relative' }
          : { display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', position: 'relative' }
        }>

          {/* Trade builder / scenarios */}
          <div style={{ flexShrink: 0, padding: '12px 20px 10px', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 9, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#86868B', fontWeight: 600, fontFamily: ff }}>
                Trade scenarios
              </div>
              {/* View toggle: bubble map / playoff picture / draft order */}
              {simDone && (
                <div style={{ display: 'flex', background: '#F5F5F7', borderRadius: 980, padding: 3, gap: 2 }}>
                  {[['scatter', 'Bubbles'], ['bracket', 'Playoffs'], ['draft', 'Draft']].map(([v, label]) => (
                    <button key={v} onClick={() => setLeftView(v)} style={{
                      border: 'none', background: leftView === v ? '#000' : 'transparent',
                      color: leftView === v ? '#fff' : '#86868B',
                      fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 980,
                      cursor: 'pointer', transition: 'all 0.18s', fontFamily: ff, whiteSpace: 'nowrap',
                    }}>{label}</button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
              {/* Baseline chip */}
              {(() => {
                const active = activeScenarioId === 'baseline' && !sandboxOpen;
                return (
                  <div onClick={selectBaseline} style={{
                    padding: '7px 14px', borderRadius: 12, flexShrink: 0,
                    border: active ? '1.5px solid #1D1D1F' : '1.5px solid #E5E5EA',
                    background: active ? '#1D1D1F' : '#fff',
                    cursor: 'pointer', transition: 'all 0.18s', minWidth: 96,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: active ? '#fff' : '#1D1D1F', fontFamily: ff }}>Baseline</div>
                    <div style={{ fontSize: 10, color: active ? 'rgba(255,255,255,0.5)' : '#86868B', fontFamily: ff }}>Current roster</div>
                  </div>
                );
              })()}

              {/* Scenario chips — the trade IS the label; clicking switches to it */}
              {scenarios.map(s => {
                const active = activeScenarioId === s.id && !sandboxOpen;
                const sAfter = s.results?.teams?.[myRosterId];
                const sBase = baseResults?.teams?.[myRosterId];
                const d = (sAfter && sBase) ? Math.round((sAfter.playoffProbability - sBase.playoffProbability) * 100) : null;
                const give = (s.summary?.give || []).map(shortName).join(', ') || '—';
                const get = (s.summary?.get || []).map(shortName).join(', ') || '—';
                const fullGive = (s.summary?.give || []).map(shortName).join(', ') || 'nothing';
                const fullGet = (s.summary?.get || []).map(shortName).join(', ') || 'nothing';
                return (
                  <div
                    key={s.id}
                    data-scenario-chip
                    onClick={() => { setHoverChip(null); selectScenario(s.id); }}
                    // Hover tooltip is desktop-only — on touch there's no mouseleave,
                    // so it would stick forever. The chip already shows give→get inline.
                    onMouseEnter={e => { if (isMobile) return; const r = e.currentTarget.getBoundingClientRect(); setHoverChip({ id: s.id, give: fullGive, get: fullGet, x: r.left, y: r.bottom + 8 }); }}
                    onMouseLeave={() => setHoverChip(null)}
                    style={{
                      position: 'relative',
                      display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
                      padding: '8px 8px 8px 14px', borderRadius: 12,
                      border: active ? '1.5px solid #1D1D1F' : '1.5px solid #E5E5EA',
                      background: active ? '#1D1D1F' : '#fff',
                      cursor: 'pointer', transition: 'all 0.18s', maxWidth: 230,
                    }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: active ? '#fff' : '#1D1D1F', fontFamily: ff, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {give} <span style={{ color: active ? 'rgba(255,255,255,0.4)' : '#C7C7CC' }}>→</span> {get}
                      </div>
                      {d != null && (
                        <div style={{ fontSize: 10, fontWeight: 700, marginTop: 2, color: d > 0 ? (active ? '#4ade80' : '#1a9640') : d < 0 ? (active ? '#ff8080' : '#c0392b') : (active ? '#aaa' : '#86868B'), fontFamily: ff }}>
                          {d > 0 ? `↑ +${d}% playoff odds` : d < 0 ? `↓ ${d}% playoff odds` : 'no change to playoff odds'}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); removeScenario(s.id); }}
                      style={{ flexShrink: 0, width: 18, height: 18, borderRadius: '50%', border: 'none', background: active ? 'rgba(255,255,255,0.18)' : '#EBEBED', color: active ? '#fff' : '#86868B', fontSize: 11, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                      title="Remove scenario"
                    >×</button>
                  </div>
                );
              })}

              {/* Add trade button — explicit so people know what this tool does */}
              {(() => {
                const firstTrade = scenarios.length === 0;
                return (
                  <button
                    onClick={handleOpenSandbox}
                    style={{
                      alignSelf: 'stretch', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 7,
                      padding: '8px 16px 8px 13px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.18s', fontFamily: ff,
                      border: '1.5px dashed #C7C7CC', background: '#fff', color: '#1D1D1F',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#1D1D1F'; e.currentTarget.style.background = '#FAFAFA'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#C7C7CC'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <span style={{ fontSize: 16, lineHeight: 1, fontWeight: 400, color: '#86868B' }}>+</span>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {firstTrade ? 'Build a trade' : 'Add a trade'}
                      </span>
                      {firstTrade && (
                        <span style={{ fontSize: 9.5, fontWeight: 500, color: '#86868B', whiteSpace: 'nowrap' }}>See how it moves your odds</span>
                      )}
                    </span>
                  </button>
                );
              })()}

              {/* League moves — model the rest of the league trading */}
              {simDone && (() => {
                const on = appliedLeague.trades.length > 0;
                const n = appliedLeague.trades.length;
                return (
                  <div
                    onClick={() => setLeagueOpen(true)}
                    style={{
                      alignSelf: 'stretch', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
                      padding: on ? '8px 8px 8px 14px' : '8px 16px 8px 13px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.18s', fontFamily: ff,
                      border: on ? '1.5px solid #1D1D1F' : '1.5px dashed #C7C7CC',
                      background: on ? '#1D1D1F' : '#fff', color: on ? '#fff' : '#1D1D1F',
                    }}
                    onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = '#1D1D1F'; e.currentTarget.style.background = '#FAFAFA'; } }}
                    onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = '#C7C7CC'; e.currentTarget.style.background = '#fff'; } }}
                  >
                    <span style={{ fontSize: 14, lineHeight: 1 }}>⚡</span>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>League moves</span>
                      <span style={{ fontSize: 9.5, fontWeight: 500, color: on ? 'rgba(255,255,255,0.5)' : '#86868B', whiteSpace: 'nowrap' }}>
                        {on ? `${n} trade${n === 1 ? '' : 's'} in play` : 'Others trade too'}
                      </span>
                    </span>
                    {on && (
                      <button
                        onClick={e => { e.stopPropagation(); clearLeague(); }}
                        style={{ flexShrink: 0, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 11, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                        title="Turn off league moves"
                      >×</button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Scatter / bracket / draft — bracket & draft need much more room on mobile.
              The pre-run explainer flows in-place on mobile (auto height) so it
              never bleeds over the hero stats below it. */}
          {(() => { const emptyStateMobile = isMobile && !simDone && !simulating; return (
          <div style={isMobile
            ? { height: emptyStateMobile ? 'auto' : (leftView === 'scatter' ? 340 : 620), minHeight: emptyStateMobile ? 360 : undefined, position: 'relative' }
            : { flex: 1, minHeight: 0, position: 'relative' }}>
            {(simulating || !simDone) && (
              <div style={{ position: emptyStateMobile ? 'relative' : 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', zIndex: 5, background: 'rgba(255,255,255,0.94)', padding: 24 }}>
                {simulating ? (
                  <SimLoading numSims={numSims} teams={bootstrap.teams} />
                ) : (
                  <div style={{ textAlign: 'center', maxWidth: 440 }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🏈</div>
                    <div style={{ fontSize: 19, fontWeight: 700, color: '#1D1D1F', marginBottom: 8, fontFamily: ff, letterSpacing: '-0.01em' }}>
                      Run {(numSims / 1000)}K Monte Carlo simulations on your league
                    </div>
                    <div style={{ fontSize: 14, color: '#6E6E73', fontFamily: ff, lineHeight: 1.5, marginBottom: 14 }}>
                      We play out the rest of the season {numSims.toLocaleString()} times — every matchup, every week, all the way through the playoffs — then count how often each team makes it. Build a trade to see how it shifts your odds.
                    </div>
                    <div style={{ fontSize: 12.5, color: '#86868B', fontFamily: ff, lineHeight: 1.5, marginBottom: 20 }}>
                      Powered by player projections and betting-market odds, refreshed daily, to model how every team stacks up.
                    </div>
                    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 8, textAlign: 'left', background: '#F5F5F7', borderRadius: 12, padding: '14px 18px' }}>
                      {[
                        ['●', '#1D1D1F', 'Each bubble is a team'],
                        ['↔', '#86868B', 'Left–right = roster scoring power (max points)'],
                        ['↕', '#86868B', 'Up–down = playoff odds'],
                        ['◉', '#86868B', 'Bubble size = championship odds'],
                      ].map(([icon, color, text]) => (
                        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: '#6E6E73', fontFamily: ff }}>
                          <span style={{ color, fontSize: 13, width: 14, textAlign: 'center' }}>{icon}</span>
                          {text}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={handleRunSim}
                      style={{ marginTop: 24, background: '#000', color: '#fff', border: 'none', borderRadius: 980, padding: '16px 40px', fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: ff, letterSpacing: '-0.01em', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', transition: 'transform 0.18s, box-shadow 0.18s' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.24)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.18)'; }}
                    >
                      Run {(numSims / 1000)}K simulations →
                    </button>
                  </div>
                )}
              </div>
            )}
            {!emptyStateMobile && (leftView === 'bracket' && simDone ? (
              <PlayoffBracket
                bootstrap={bootstrap}
                results={view === 'after' && tradeResults ? tradeResults : baseResults}
                myRosterId={myRosterId}
                onSelectTeam={handleSelectTeam}
              />
            ) : leftView === 'draft' && simDone ? (
              <DraftOrder
                bootstrap={bootstrap}
                results={view === 'after' && tradeResults ? tradeResults : baseResults}
                myRosterId={myRosterId}
                tradeAdjustments={view === 'after' ? tradeAdjustments : null}
                onSelectTeam={handleSelectTeam}
              />
            ) : (
              <ScatterChart
                bootstrap={bootstrap}
                results={baseResults}
                afterResults={tradeResults}
                view={view}
                selectedTeamId={selectedTeamId}
                onSelectTeam={handleSelectTeam}
                myRosterId={myRosterId}
              />
            ))}
          </div>
          ); })()}

          {/* Trade sandbox — drawer slides over the left panel (absolute, shell-safe) */}
          {sandboxOpen && (
            <div style={{ position: 'absolute', inset: 0, background: '#F5F5F7', zIndex: 60, overflowY: 'auto', padding: isMobile ? 14 : 20 }}>
              <TradeSandbox
                bootstrap={bootstrap}
                myRosterId={myRosterId}
                results={baseResults}
                onTradeChange={handleApplyTrade}
                onClose={() => setSandboxOpen(false)}
                isSuperFlex={bootstrap.isSuperFlex}
              />
            </div>
          )}

          {/* League moves — panel slides over the left column (absolute, shell-safe) */}
          {leagueOpen && (
            <div style={{ position: 'absolute', inset: 0, background: '#F5F5F7', zIndex: 60, overflowY: 'auto', padding: isMobile ? 14 : 20 }}>
              <LeagueMoves
                bootstrap={bootstrap}
                baseResults={pureBaseResults || baseResults}
                myRosterId={myRosterId}
                initialMoves={appliedLeague.moves}
                initialTags={leagueTags}
                initialExcluded={appliedLeague.excluded || []}
                initialSeed={appliedLeague.seed}
                initialLocked={appliedLeague.locked || []}
                onApply={applyLeague}
                onClose={() => setLeagueOpen(false)}
              />
            </div>
          )}
        </div>

        {/* ── Right: Hero stats + Range + Team list — inline on mobile, shell rail on desktop ── */}
        <ForecastSlot isMobile={isMobile}>

          {/* Hero stats */}
          <div style={{ background: '#1D1D1F', padding: '16px 20px 14px', flexShrink: 0, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 12, fontFamily: ff }}>
              {myTeam?.displayName || 'Your team'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              {[
                { label: 'Make playoffs', key: 'playoffProbability', id: 'pl' },
                { label: 'Win championship', key: 'championshipProbability', id: 'ch' },
              ].map(({ label, key, id }) => {
                const baseVal = myBase ? myBase[key] * 100 : null;
                const afterVal = myAfter ? myAfter[key] * 100 : null;
                const delta = afterVal != null && baseVal != null ? afterVal - baseVal : null;
                const displayVal = view === 'after' && afterVal != null ? afterVal : baseVal;
                const tickDirection = delta > 0 ? 'up' : delta < 0 ? 'down' : '';
                return (
                  <div key={id} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '11px 13px' }}>
                    <div style={{ fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 5, fontFamily: ff }}>{label}</div>
                    <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.04em', color: '#fff', lineHeight: 1, marginBottom: 5, fontFamily: ff }}>
                      <HeroStatValue value={displayVal} decimals={id === 'ch' ? 1 : 0} tickDirection={tickDirection} />
                      <span style={{ fontSize: 15, fontWeight: 500, color: 'rgba(255,255,255,0.4)', letterSpacing: 0 }}>%</span>
                    </div>
                    {delta != null && Math.abs(delta) > 0.05 && (
                      <div className={`ft-fade ${deltaReveal ? 'ft-play' : ''}`} style={{
                        display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700,
                        padding: '3px 8px', borderRadius: 980,
                        background: delta > 0 ? 'rgba(48,212,122,0.15)' : 'rgba(255,100,100,0.15)',
                        color: delta > 0 ? '#30d47a' : '#ff8080',
                        fontFamily: ff,
                      }}>
                        {delta > 0 ? '↑ +' : '↓ '}{Math.abs(id === 'ch' ? delta.toFixed(1) : Math.round(delta))}{id === 'pl' ? '% playoffs' : '% title'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Plain-language read of the trade — settles in after the count-up
                and delta pill above have had a beat to land. */}
            {verdict && (
              <div className={`ft-fade ${verdictReveal ? 'ft-play' : ''}`} style={{
                fontSize: 12, fontWeight: 600, marginBottom: 8, fontFamily: ff,
                color: verdict.tone === 'up' ? '#30d47a' : verdict.tone === 'down' ? '#ff8080' : 'rgba(255,255,255,0.5)',
              }}>
                {verdict.text}
              </div>
            )}

            {/* Max PF bar — follows the active scenario (base or trade) */}
            {myBase && (() => {
              const activeResults = view === 'after' && tradeResults ? tradeResults : baseResults;
              const myActive = (view === 'after' && myAfter) ? myAfter : myBase;
              const mp = myActive.maxPointsFor?.p50 || 0;
              const delta = mp - (myBase.maxPointsFor?.p50 || 0);
              const allMp = bootstrap.teams.map(t => activeResults?.teams[t.rosterId]?.maxPointsFor?.p50 || 0);
              const minMp = Math.min(...allMp), maxMp = Math.max(...allMp);
              const pct = maxMp > minMp ? ((mp - minMp) / (maxMp - minMp)) * 100 : 50;
              const rank = [...allMp].sort((a, b) => b - a).indexOf(mp) + 1;
              const showDelta = view === 'after' && myAfter && Math.abs(delta) >= 1;
              return (
                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '10px 13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 3, fontFamily: ff }}>Max points</div>
                    <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.025em', color: '#fff', fontFamily: ff }}>{Math.round(mp).toLocaleString()}</div>
                    {showDelta && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 700, marginTop: 5, padding: '2px 7px', borderRadius: 980, background: delta > 0 ? 'rgba(48,212,122,0.15)' : 'rgba(255,100,100,0.15)', color: delta > 0 ? '#30d47a' : '#ff8080', fontFamily: ff }}>
                        {delta > 0 ? '↑ +' : '↓ '}{Math.abs(Math.round(delta)).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.7)', fontFamily: ff }}>{rank}{['st','nd','rd'][rank-1]||'th'}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', fontFamily: ff }}>of {bootstrap.teams.length}</div>
                    <div style={{ width: 72, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, position: 'relative' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${pct}%`, background: 'rgba(255,255,255,0.4)', borderRadius: 2 }} />
                      <div style={{ position: 'absolute', top: '50%', left: `${pct}%`, transform: 'translate(-50%,-50%)', width: 8, height: 8, background: '#fff', borderRadius: '50%', border: '2px solid #1D1D1F' }} />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Promoted trade CTA — once a baseline exists and no trade has been
              tried yet, this is the dominant action (not the small dashed
              pill up in the scenario row), anchored right under the odds it
              would change. Disappears once a scenario exists. */}
          {simDone && scenarios.length === 0 && (
            <div className={`ft-fade ${tradeCtaReveal ? 'ft-play' : ''}`} style={{ flexShrink: 0, padding: '20px 16px 22px', background: '#fff' }}>
              <button
                onClick={handleOpenSandbox}
                style={{
                  width: '100%', background: '#000', color: '#fff', border: 'none', borderRadius: 980,
                  padding: '15px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: ff,
                  letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  transition: 'transform 0.18s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 24px rgba(0,0,0,0.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                See how a trade changes this →
              </button>
            </div>
          )}

          {/* Range of finishes — concrete win distribution (replaces the fan chart) */}
          {myBase && (
            <div style={{ flexShrink: 0, padding: '12px 16px 14px', background: '#fff', borderTop: '1px solid rgba(0,0,0,0.06)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 9, letterSpacing: '2px', textTransform: 'uppercase', color: '#86868B', fontWeight: 600, marginBottom: 10, fontFamily: ff }}>
                Range of finishes
              </div>
              {(() => {
                const showAfter = view === 'after' && myAfter;
                const data = showAfter ? myAfter : myBase;
                const hist = data.winHistogram || [];
                const cut = Math.round((bootstrap.regularSeasonWeeks || 14) * (bootstrap.playoffTeams || 6) / bootstrap.teams.length);
                const maxP = Math.max(...hist.map(h => h.prob), 0.01);
                const mode = hist.reduce((a, b) => (b.prob > (a?.prob || 0) ? b : a), null);
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 56 }}>
                      {hist.map(h => {
                        const inP = h.wins >= cut;
                        return (
                          <div key={h.wins} title={`${h.wins} wins: ${Math.round(h.prob * 100)}%`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                            <div style={{ width: '100%', height: `${(h.prob / maxP) * 100}%`, minHeight: h.prob > 0 ? 2 : 0, background: inP ? 'rgba(52,199,89,0.7)' : '#D8D8DC', borderRadius: '2px 2px 0 0' }} />
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 9, color: '#C7C7CC', fontFamily: ff }}>
                      <span>0 W</span><span style={{ color: 'rgba(52,199,89,0.8)' }}>playoff range →</span><span>{hist.length - 1} W</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6E6E73', marginTop: 8, lineHeight: 1.5, fontFamily: ff }}>
                      Most likely <strong style={{ color: '#1D1D1F', whiteSpace: 'nowrap' }}>{mode?.wins ?? '—'} wins</strong>.<br />
                      80% of seasons finish <strong style={{ color: '#1D1D1F', whiteSpace: 'nowrap' }}>{(data.wins?.p10 ?? 0).toFixed(0)}–{(data.wins?.p90 ?? 0).toFixed(0)} wins</strong>.
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Team list */}
          <TeamList
            bootstrap={bootstrap}
            results={baseResults}
            afterResults={tradeResults}
            view={view}
            myRosterId={myRosterId}
            selectedTeamId={selectedTeamId}
            onSelectTeam={handleSelectTeam}
          />
        </ForecastSlot>
      </div>

      {/* Team detail modal */}
      {detailTeamId != null && simDone && (() => {
        const dt = (view === 'after' && tradeResults ? tradeResults : baseResults);
        const team = bootstrap.teams.find(t => t.rosterId === detailTeamId);
        const teamResult = dt?.teams?.[detailTeamId];
        if (!team || !teamResult) return null;
        // Rank within current view
        const ranked = [...bootstrap.teams].sort((a, b) =>
          (dt.teams[b.rosterId]?.playoffProbability || 0) - (dt.teams[a.rosterId]?.playoffProbability || 0));
        const rank = ranked.findIndex(t => t.rosterId === detailTeamId) + 1;
        return (
          <TeamDetailModal
            team={team}
            result={teamResult}
            bootstrap={bootstrap}
            rank={rank}
            isMe={detailTeamId === myRosterId}
            onClose={() => setDetailTeamId(null)}
          />
        );
      })()}

      {/* Scenario tooltip — fixed so it escapes the horizontally-scrolling chip row */}
      {hoverChip && (
        <div style={{
          position: 'fixed', top: hoverChip.y, left: hoverChip.x, zIndex: 200,
          background: '#1D1D1F', borderRadius: 12, padding: '11px 14px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.22)', minWidth: 200, maxWidth: 300,
          pointerEvents: 'none', fontFamily: ff,
        }}>
          <div style={{ position: 'absolute', top: -5, left: 22, width: 10, height: 10, background: '#1D1D1F', transform: 'rotate(45deg)', borderRadius: 2 }} />
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 3 }}>You give</div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#ff8a80', lineHeight: 1.35, marginBottom: 9 }}>{hoverChip.give}</div>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 3 }}>You get</div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#7ee2a8', lineHeight: 1.35 }}>{hoverChip.get}</div>
        </div>
      )}

      {/* "How this works" — plain-language explainer (no secret sauce) */}
      {infoOpen && (
        <div onClick={() => setInfoOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 310, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: ff }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: isMobile ? '26px 22px' : '32px 30px', width: '100%', maxWidth: 460, boxShadow: '0 24px 60px rgba(0,0,0,0.28)', maxHeight: '86vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 21, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.02em' }}>How this works</div>
              <button onClick={() => setInfoOpen(false)} aria-label="Close" style={{ flexShrink: 0, width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#F5F5F7', color: '#6E6E73', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', marginBottom: 6 }}>What's a Monte Carlo simulation?</div>
                <div style={{ fontSize: 14.5, color: '#1D1D1F', lineHeight: 1.55 }}>
                  Instead of guessing one outcome, we play out the rest of your season <strong>{numSims.toLocaleString()} times</strong> — every matchup, every week, through the playoffs — with realistic week-to-week swings each time. Then we count how often each result happens. So “78% to make the playoffs” means you made it in 78% of those simulated seasons.
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', marginBottom: 6 }}>Where the numbers come from</div>
                <div style={{ fontSize: 14.5, color: '#1D1D1F', lineHeight: 1.55 }}>
                  Each team's scoring is estimated from up-to-date <strong>player projections</strong> and <strong>betting-market odds</strong>, refreshed daily — so the model reflects this season, not last year. Trades and league moves just change the rosters before we re-run.
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: '#86868B', lineHeight: 1.5, background: '#F5F5F7', borderRadius: 12, padding: '12px 14px' }}>
                More simulations = smoother odds. The numbers will wiggle a percent or two between runs — that's the randomness, working as intended.
              </div>
            </div>
            <button onClick={() => setInfoOpen(false)} style={{ marginTop: 22, width: '100%', background: '#000', color: '#fff', border: 'none', borderRadius: 980, padding: '14px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: ff }}>Got it</button>
          </div>
        </div>
      )}

      {/* Quick save modal — email a magic link, then auto-save on return */}
      {authModal && (
        <div onClick={() => setAuthModal(null)} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: ff }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: '32px 28px', width: '100%', maxWidth: 380, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', textAlign: 'center' }}>
            {authModal === 'email' ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.02em', marginBottom: 8 }}>Save your scenarios</div>
                <div style={{ fontSize: 14, color: '#6E6E73', lineHeight: 1.5, marginBottom: 22 }}>
                  Enter your email and we'll send a one-tap link. Your {scenarios.length === 1 ? 'trade' : `${scenarios.length} trades`} save automatically when you come back.
                </div>
                <form onSubmit={handleEmailSubmit}>
                  <input
                    type="email" autoFocus required value={authEmail}
                    onChange={e => setAuthEmail(e.target.value)} placeholder="you@email.com"
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F5F5F7', border: 'none', borderRadius: 12, padding: '15px 16px', fontSize: 16, color: '#1D1D1F', outline: 'none', marginBottom: 12, fontFamily: ff }}
                  />
                  <button type="submit" style={{ width: '100%', background: '#000', color: '#fff', border: 'none', borderRadius: 980, padding: '15px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: ff }}>
                    Email me a save link
                  </button>
                </form>
                <button onClick={() => setAuthModal(null)} style={{ marginTop: 14, background: 'none', border: 'none', color: '#86868B', fontSize: 13, cursor: 'pointer', fontFamily: ff }}>Not now</button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 34, marginBottom: 10 }}>✉️</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.02em', marginBottom: 8 }}>Check your email</div>
                <div style={{ fontSize: 14, color: '#6E6E73', lineHeight: 1.5, marginBottom: 22 }}>
                  We sent a link to <strong style={{ color: '#1D1D1F' }}>{authEmail}</strong>. Tap it and your scenarios save automatically.
                </div>
                <button onClick={() => setAuthModal(null)} style={{ background: '#F5F5F7', color: '#1D1D1F', border: 'none', borderRadius: 980, padding: '13px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: ff }}>Done</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
